import React, { useState, useMemo } from 'react';
import { Button, Checkbox } from '@material-ui/core';
import { Edit, Trash, CirclePlus, AlertTriangle } from 'tabler-icons-react';
import { useCalidad, CAT_PARTES, CAT_DEFECTOS } from './CalidadContext';
import SearchInput from '../../components/SearchInput';
import ItemModal from './ItemModal';
import ConfirmModal from '../../components/ConfirmModal';
import filtrarItems from './filtrarItems';

export default function PartesDefectos() {
    const {
        partes,
        defectos,
        loading,
        postItem,
        putItem,
        deleteItem,
        errorMsg,
        setErrorMsg,
        parteSeleccionada,
        seleccionarParte,
        seleccionDefectos,
        toggleDefecto,
        guardarAsociaciones,
        guardando,
        dirty,
        parteSinRestricciones,
    } = useCalidad();

    const [busquedaPartes, setBusquedaPartes] = useState('');
    const [busquedaDefectos, setBusquedaDefectos] = useState('');
    const [modal, setModal] = useState(null); // { categoria, item }
    const [porEliminar, setPorEliminar] = useState(null); // { categoria, item }
    const [confirmarVacio, setConfirmarVacio] = useState(false);
    const [parteObjetivo, setParteObjetivo] = useState(null);

    const partesVisibles = useMemo(
        () => filtrarItems(partes, busquedaPartes),
        [partes, busquedaPartes]
    );
    const defectosVisibles = useMemo(
        () => filtrarItems(defectos, busquedaDefectos),
        [defectos, busquedaDefectos]
    );

    const abrirModal = (categoria, item) => {
        setErrorMsg(null);
        setModal({ categoria, item });
    };

    const cerrarModal = () => {
        setModal(null);
        setErrorMsg(null);
    };

    const guardarItem = async (nombre) => {
        const { categoria, item } = modal;
        const ok = item
            ? await putItem(categoria, item.codigo, nombre)
            : await postItem(categoria, nombre);
        if (ok) cerrarModal();
    };

    // Cambiar de parte con cambios pendientes pide confirmación antes de descartarlos.
    const intentarSeleccionar = (parte) => {
        if (parte.codigo === parteSeleccionada?.codigo) return;
        if (dirty) {
            setParteObjetivo(parte);
            return;
        }
        seleccionarParte(parte);
    };

    // Guardar vacío no deja la parte sin defectos: la abre a todos. Se confirma antes.
    const intentarGuardar = () => {
        if (seleccionDefectos.size === 0) {
            setConfirmarVacio(true);
            return;
        }
        guardarAsociaciones();
    };

    const etiquetaModal = () => {
        if (!modal) return '';
        const nombre = modal.categoria === CAT_PARTES ? 'Parte' : 'Tipo de defecto';
        return `${modal.item ? 'Editar' : 'Nueva'} — ${nombre}`;
    };

    return (
        <div className="cal-split">
            {/* ── Partes ── */}
            <div className="cap-card cal-panel-partes">
                <div className="cap-card-toolbar">
                    <h3 className="cap-card-title">Partes</h3>
                    <SearchInput value={busquedaPartes} onChange={setBusquedaPartes} />
                </div>
                <div className="cal-lista">
                    {loading && partes.length === 0 && <p className="desc">Cargando...</p>}
                    {!loading && partes.length === 0 && (
                        <p className="desc">
                            No hay partes registradas. Agrega la primera para asociarle defectos.
                        </p>
                    )}
                    {partes.length > 0 && partesVisibles.length === 0 && (
                        <p className="desc">Sin resultados para «{busquedaPartes}»</p>
                    )}
                    {partesVisibles.map((parte) => (
                        <div
                            key={parte.id}
                            className={`cal-row cal-row--click${
                                parteSeleccionada?.codigo === parte.codigo ? ' cal-row--sel' : ''
                            }`}
                            onClick={() => intentarSeleccionar(parte)}
                        >
                            <span className="cal-row-name">{parte.nombre}</span>
                            <div className="cal-row-actions">
                                <button
                                    className="cap-catalog-btn"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        abrirModal(CAT_PARTES, parte);
                                    }}
                                >
                                    <Edit size={16} />
                                </button>
                                <button
                                    className="cap-catalog-btn cap-catalog-btn--delete"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setPorEliminar({ categoria: CAT_PARTES, item: parte });
                                    }}
                                >
                                    <Trash size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                    <button className="cal-add-row" onClick={() => abrirModal(CAT_PARTES, null)}>
                        <CirclePlus size={18} />
                        <span>Agregar parte</span>
                    </button>
                </div>
            </div>

            {/* ── Defectos ── */}
            <div className="cap-card cal-panel-defectos">
                <div className="cap-card-toolbar">
                    <h3 className="cap-card-title">
                        {parteSeleccionada
                            ? `Defectos de «${parteSeleccionada.nombre}»`
                            : 'Tipos de defecto'}
                    </h3>
                    <div className="cal-toolbar-right">
                        <SearchInput value={busquedaDefectos} onChange={setBusquedaDefectos} />
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            disabled={!parteSeleccionada || !dirty || guardando}
                            onClick={intentarGuardar}
                        >
                            {guardando ? 'Guardando...' : 'Guardar'}
                        </Button>
                    </div>
                </div>

                {!parteSeleccionada && (
                    <p className="cal-hint">
                        Selecciona una parte para marcar qué defectos aplican. También puedes
                        administrar el catálogo de defectos desde aquí.
                    </p>
                )}

                {parteSeleccionada && parteSinRestricciones && !dirty && (
                    <div className="cal-banner">
                        <AlertTriangle size={18} />
                        <span>
                            Esta parte no tiene defectos acotados, por lo que acepta{' '}
                            <strong>todos</strong> los defectos.
                        </span>
                    </div>
                )}

                {dirty && (
                    <div className="cal-banner cal-banner--dirty">
                        <AlertTriangle size={18} />
                        <span>Tienes cambios sin guardar.</span>
                    </div>
                )}

                <div className="cal-lista">
                    {loading && defectos.length === 0 && <p className="desc">Cargando...</p>}
                    {defectos.length > 0 && defectosVisibles.length === 0 && (
                        <p className="desc">Sin resultados para «{busquedaDefectos}»</p>
                    )}
                    {defectosVisibles.map((defecto) => (
                        <div
                            key={defecto.id}
                            className={`cal-row${
                                seleccionDefectos.has(defecto.codigo) ? ' cal-row--on' : ''
                            }`}
                        >
                            <label className="cal-row-check">
                                <Checkbox
                                    color="primary"
                                    size="small"
                                    disabled={!parteSeleccionada}
                                    checked={seleccionDefectos.has(defecto.codigo)}
                                    onChange={() => toggleDefecto(defecto.codigo)}
                                />
                                <span className="cal-row-name">{defecto.nombre}</span>
                            </label>
                            <div className="cal-row-actions">
                                <button
                                    className="cap-catalog-btn"
                                    onClick={() => abrirModal(CAT_DEFECTOS, defecto)}
                                >
                                    <Edit size={16} />
                                </button>
                                <button
                                    className="cap-catalog-btn cap-catalog-btn--delete"
                                    onClick={() =>
                                        setPorEliminar({ categoria: CAT_DEFECTOS, item: defecto })
                                    }
                                >
                                    <Trash size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                    <button className="cal-add-row" onClick={() => abrirModal(CAT_DEFECTOS, null)}>
                        <CirclePlus size={18} />
                        <span>Agregar tipo de defecto</span>
                    </button>
                </div>
            </div>

            <ItemModal
                open={!!modal}
                onClose={cerrarModal}
                onSubmit={guardarItem}
                titulo={etiquetaModal()}
                item={modal?.item || null}
                errorMsg={errorMsg}
            />

            <ConfirmModal
                open={!!porEliminar}
                onClose={() => setPorEliminar(null)}
                onConfirm={async () => {
                    await deleteItem(porEliminar.categoria, porEliminar.item.codigo);
                    setPorEliminar(null);
                }}
                title={`Eliminar — ${porEliminar?.categoria === CAT_PARTES ? 'Parte' : 'Tipo de defecto'}`}
            >
                ¿Estás seguro de eliminar <strong>{porEliminar?.item?.nombre}</strong>? Esta acción
                no se puede deshacer.
            </ConfirmModal>

            <ConfirmModal
                open={confirmarVacio}
                onClose={() => setConfirmarVacio(false)}
                onConfirm={async () => {
                    setConfirmarVacio(false);
                    await guardarAsociaciones();
                }}
                title="Guardar sin defectos"
                confirmText="Guardar de todos modos"
            >
                No marcaste ningún defecto para <strong>{parteSeleccionada?.nombre}</strong>. Eso no
                la deja sin defectos: la parte pasará a aceptar <strong>todos</strong> los defectos
                del catálogo.
            </ConfirmModal>

            <ConfirmModal
                open={!!parteObjetivo}
                onClose={() => setParteObjetivo(null)}
                onConfirm={() => {
                    seleccionarParte(parteObjetivo);
                    setParteObjetivo(null);
                }}
                title="Cambios sin guardar"
                confirmText="Descartar cambios"
            >
                Los cambios en los defectos de <strong>{parteSeleccionada?.nombre}</strong> no se
                han guardado. Si cambias a <strong>{parteObjetivo?.nombre}</strong> se perderán.
            </ConfirmModal>
        </div>
    );
}
